import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';
import { Icon } from './Icon';
import { useTip } from './Tip';
import type { Tone } from '../lib/labels';

interface StatTileProps {
  readonly tone: Tone;
  readonly label: string;
  readonly value: ReactNode;
  /**
   * A explicação do número, que agora vive no hover.
   *
   * Ela era um parágrafo dentro do tile, e seis parágrafos lado a lado transformavam a
   * primeira tela do produto num texto: o número, que é o assunto, competia com a
   * explicação dele. No hover a explicação continua a um gesto de distância, e o painel
   * volta a ser legível de longe.
   */
  readonly hint?: string;
  /** O tile inteiro vira link. Alvo grande, e sem uma segunda linha de "abrir". */
  readonly to?: string;
  /** O que precisa ser visto primeiro. Só a borda muda: dois destaques já são nenhum. */
  readonly emphasis?: boolean;
}

export function StatTile({
  tone,
  label,
  value,
  hint,
  to,
  emphasis = false,
}: StatTileProps): ReactElement {
  const tip = useTip();
  const classe = `stat stat--${tone}${emphasis ? ' stat--emphasis' : ''}${
    to === undefined ? '' : ' stat--link'
  }`;
  const dica = hint === undefined ? {} : tip(hint);

  /* O número vem PRIMEIRO, grande, e o rótulo ao lado dele.
     Com o rótulo em cima, a frase quebrava em duas linhas e empurrava o número para
     baixo: o tile parecia empilhado, e o que se lê de longe num painel é o número. O
     ícone saiu junto — a cor do fundo e o rótulo já dizem do que se trata, e um chip
     por tile era o terceiro elemento disputando a mesma linha. */
  const dentro = (
    <>
      <span className="stat__value num">{value}</span>
      <span className="stat__label">{label}</span>
      {to === undefined ? null : (
        <span className="stat__seta">
          <Icon name="chevron-right" size={15} />
        </span>
      )}
    </>
  );

  return to === undefined ? (
    <div className={classe} {...dica}>
      {dentro}
    </div>
  ) : (
    <Link to={to} className={classe} {...dica}>
      {dentro}
    </Link>
  );
}
